import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../api";
import { ErrorNote, Loading, StatCard, StatusTag, secsToHHMM } from "../components/ui";
import { MonthCalendar } from "../components/charts2";
import { Donut, TrendLine } from "../components/charts3";
import { useRoles } from "../hooks";
import type { EmployeeProfile as Profile } from "../types";

function shiftMonth(ym: string, delta: number): string {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export default function EmployeeProfile() {
  const { id = "" } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { isAdmin } = useRoles();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [month, setMonth] = useState<string>(sp.get("month") ?? "");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.profile(id, month ? { month } : {})
      .then((p) => {
        setProfile(p);
        if (!month && p.monthly.length) setMonth(p.monthly[p.monthly.length - 1].period);
        setError(null);
      })
      .catch((e: ApiError) => setError(e.status === 404
        ? "No records found for this employee in the selected range." : e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, month]);

  const byDate = useMemo(() => {
    const map: Record<string, { inside_hhmm: string; inside_seconds: number; status: string }> = {};
    (profile?.records ?? []).filter((r) => r.date.startsWith(month)).forEach((r) => {
      map[r.date] = { inside_hhmm: r.inside_hhmm, inside_seconds: r.inside_seconds, status: r.status };
    });
    return map;
  }, [profile, month]);

  const monthRecords = (profile?.records ?? []).filter((r) => r.date.startsWith(month));

  const download = async () => {
    if (!profile) return;
    const blob = await api.exportReport({ employee_id: id, location: profile.location || undefined }, "csv");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `employee-${id}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const deleteEmp = async () => {
    if (!profile) return;
    const typed = window.prompt(
      `This permanently deletes ALL records for ${profile.employee_name} (${profile.employee_id}) at `
      + `${profile.location || "—"} — use for resigned employees.\n\nType the Employee ID to confirm:`);
    if (typed === null) return;
    if (typed.trim() !== profile.employee_id) { alert("Employee ID did not match. Nothing deleted."); return; }
    try {
      const r = await api.deleteEmployee(profile.employee_id, profile.location);
      alert(`Deleted: ${r.daily_deleted} day rows, ${r.events_deleted} events.`);
      navigate("/employees");
    } catch (e) { setError((e as ApiError).message); }
  };

  const [y, m] = month ? month.split("-").map(Number) : [0, 0];

  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h1 className="h1">Employee Profile</h1>
        <Link to="/employees" className="navlink">← Search</Link>
      </div>

      {loading && <Loading what="profile" />}
      {error && <ErrorNote message={error} />}

      {profile && !loading && (
        <>
          <div className="panel">
            <div className="row" style={{ justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{profile.employee_name || "—"}</div>
                <div className="notice">
                  ID {profile.employee_id} · {profile.department || "—"} · {profile.location || "—"}
                </div>
                <div className="notice">
                  First access {profile.first_access ?? "—"} · Last access {profile.last_access ?? "—"}
                </div>
                <div style={{ marginTop: 6 }}>
                  {profile.policy_group
                    ? <span className="tag ok">Policy: {profile.policy_group.name}
                        {profile.policy_group.location ? ` · ${profile.policy_group.location}` : ""}
                        {profile.policy_group.exempt_rules?.length ? ` · exempt: ${profile.policy_group.exempt_rules.join(",")}` : ""}</span>
                    : <span className="tag review">No policy group assigned</span>}
                </div>
              </div>
              <div className="row">
                <button className="primary" onClick={download}>Download report (CSV)</button>
                {isAdmin && (
                  <button className="ghost" style={{ color: "var(--incomplete)", borderColor: "var(--incomplete)" }}
                          onClick={deleteEmp}>Delete employee</button>
                )}
              </div>
            </div>
          </div>

          <div className="cards">
            <StatCard label="Present days" value={profile.present_days} />
            <StatCard label="Total inside" value={profile.total_inside_hhmm} />
            <StatCard label="Avg daily" value={profile.avg_daily_hhmm} />
            <StatCard label="Avg arrival" value={profile.avg_arrival ?? "—"} />
            <StatCard label="Avg exit" value={profile.avg_exit ?? "—"} />
            <StatCard label="Incomplete" value={profile.incomplete_days} />
          </div>

          <div className="chart-grid">
            <div className="panel" style={{ marginTop: 0 }}>
              <h2>Day-quality breakdown</h2>
              <Donut data={[
                { name: "Complete", value: Math.max(0, profile.present_days - profile.review_days), color: "#148f5b" },
                { name: "Review", value: profile.review_days, color: "#b7791f" },
                { name: "Incomplete", value: profile.incomplete_days, color: "#d14343" },
              ]} />
              <p className="notice">Recorded office presence (not working hours). Absence/leave needs roster data.</p>
            </div>
            <div className="panel" style={{ marginTop: 0 }}>
              <h2>Monthly inside-office hours</h2>
              <TrendLine data={profile.monthly.map((m) => ({ period: m.period, total_inside_seconds: m.total_inside_seconds }))}
                         xKey="period" unit="hours" kind="area"
                         lines={[{ key: "total_inside_seconds", name: "Inside hours" }]} />
            </div>
          </div>

          <div className="panel">
            <h2>Monthly summary</h2>
            <table>
              <thead>
                <tr><th>Month</th><th>Days</th><th>Total inside</th><th>Avg daily</th>
                  <th>Avg arrival</th><th>Avg exit</th><th>Incomplete</th></tr>
              </thead>
              <tbody>
                {profile.monthly.map((mo) => (
                  <tr key={mo.period} onClick={() => setMonth(mo.period)}
                      style={{ cursor: "pointer", background: mo.period === month ? "var(--surface-2)" : undefined }}>
                    <td><strong>{mo.period}</strong></td>
                    <td>{mo.present_days}</td>
                    <td>{mo.total_inside_hhmm}</td>
                    <td>{secsToHHMM(mo.avg_daily_seconds)}</td>
                    <td>{mo.avg_arrival ?? "—"}</td>
                    <td>{mo.avg_exit ?? "—"}</td>
                    <td>{mo.incomplete_days || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="notice">Click a month to view its calendar and daily records below.</p>
          </div>

          <div className="panel">
            <h2>Weekly summary</h2>
            <div style={{ overflowX: "auto" }}>
              <table className="tabular">
                <thead>
                  <tr><th>Week</th><th>Days</th><th>Total inside</th><th>Avg daily</th>
                    <th>Avg first-in</th><th>Avg last-out</th><th>Incomplete</th></tr>
                </thead>
                <tbody>
                  {profile.weekly.map((w) => (
                    <tr key={w.period}>
                      <td>{w.period}</td>
                      <td>{w.present_days}</td>
                      <td>{w.total_inside_hhmm}</td>
                      <td>{secsToHHMM(w.avg_daily_seconds)}</td>
                      <td>{w.avg_arrival ?? "—"}</td>
                      <td>{w.avg_exit ?? "—"}</td>
                      <td>{w.incomplete_days || ""}</td>
                    </tr>
                  ))}
                  {profile.weekly.length === 0 && <tr><td colSpan={7} className="empty">No weeks in range.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {month && (
            <div className="panel">
              <div className="row" style={{ justifyContent: "space-between" }}>
                <h2 style={{ margin: 0 }}>{month}</h2>
                <div className="row">
                  <button className="ghost" onClick={() => setMonth(shiftMonth(month, -12))}>« Year</button>
                  <button className="ghost" onClick={() => setMonth(shiftMonth(month, -1))}>‹ Month</button>
                  <button className="ghost" onClick={() => setMonth(shiftMonth(month, 1))}>Month ›</button>
                  <button className="ghost" onClick={() => setMonth(shiftMonth(month, 12))}>Year »</button>
                </div>
              </div>
              <MonthCalendar year={y} month={m} byDate={byDate} />
            </div>
          )}

          <div className="panel">
            <h2>Daily records — {month}</h2>
            <table>
              <thead>
                <tr><th>Date</th><th>First</th><th>Last</th><th>Inside</th><th>Outside</th>
                  <th>Intervals</th><th>Status</th></tr>
              </thead>
              <tbody>
                {monthRecords.map((r) => {
                  const ivals = profile.intervals_by_day[r.date] ?? [];
                  return (
                    <tr key={r.date}>
                      <td>{r.date}</td>
                      <td>{r.first_entry?.slice(11, 19) ?? "—"}</td>
                      <td>{r.last_exit?.slice(11, 19) ?? "—"}</td>
                      <td>{r.inside_hhmm}</td>
                      <td>{secsToHHMM(r.outside_seconds)}</td>
                      <td title={ivals.map((i) => `${i.start.slice(11, 16)}–${i.end.slice(11, 16)}`).join(", ")}>
                        {ivals.length ? ivals.map((i) => `${i.start.slice(11, 16)}–${i.end.slice(11, 16)}`).join(", ") : r.matched_intervals}
                      </td>
                      <td><StatusTag status={r.status} /></td>
                    </tr>
                  );
                })}
                {monthRecords.length === 0 && <tr><td colSpan={7} className="notice">No records this month.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
